import React, { FormEvent, useEffect, useState } from 'react';
import Appbar from 'components/appbar/Appbar';
import { Chip } from '@mui/material';
import { percentFormat } from 'helpers/moneyFormat';
import ProcedureForm from '../form/ProcedureForm';
import { useProcedureValidation } from '../validation/useProcedureValidation';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import history from 'services/history';
import InsideSaving from 'components/loading/InsideSaving';
import { ProcedurePackage, Anesthetist, ProcedureDoctor, ProcedureProduct, ProcedureSpecialty } from 'types/procedure';
import { Procedure } from 'types/procedure';
import ProcedureTab from '../tab/ProcedureTab';
import ProcedureProducts from '../ProcedureProducts';
import ProcedurePackages from '../ProcedurePackages';
import ProcedureDoctors from '../ProcedureDoctors';
import ProcedureAnesthetistForm from '../form/ProcedureAnesthetistForm';
import ProcedureSpecialtiesForm from '../form/ProcedureSpecialtiesForm';
import { Specialty } from 'types/specialty';
import {
  ContentPad,
  PageShell,
  RecordActions,
  RecordHeader,
  RecordNote,
  RecordSection,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';

const NewProcedure: React.FC = () => {
  const [validation, setValidation, validate] = useProcedureValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [anesthetist, setAnesthetist] = useState<Anesthetist>({
    sale_price_max: 0,
    sale_price_min: 0,
  });
  const [procedure, setProcedure] = useState<Procedure>({
    id: 0,
    name: '',
    anesthetist: null,
    active: true,
    doctors: [],
    packages: [],
    products: [],
    specialties: [],
    profit_margin: 0,
    sale_price: 0,
    commission: 0,
    cost: 0,
    type: 'P',
    sum_commission_to_total: false,
  });
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);

  useEffect(() => {
    setLoadingSpecialties(true);

    api
      .get('/specialties/all')
      .then(response => {
        setSpecialties(response.data);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar as especialidades');
      })
      .finally(() => {
        setLoadingSpecialties(false);
      });
  }, []);

  function handleChangeAnesthetist(index: keyof Anesthetist, value: any) {
    setAnesthetist(prev => ({ ...prev, [index]: value }));
  }

  function handleChange(index: keyof Procedure, value: any) {
    if (index === 'specialties') {
      const updatedSpecialties: ProcedureSpecialty[] = specialties.map(specialty => ({
        specialty_id: specialty.id,
        active: (value as number[]).includes(specialty.id) ? 1 : 0,
        id: specialty.id,
        specialty_name: specialty.name,
        specialty: specialty,
      }));
      setProcedure(prev => ({ ...prev, specialties: updatedSpecialties }));
      return;
    }
    setProcedure(prev => ({ ...prev, [index]: value }));
  }

  function handleSubmit() {
    setSaving(true);

    api
      .post('/procedures', { ...procedure, anesthetist })
      .then(() => {
        toast.success('Procedimento cadastrado com sucesso!');
        history.push('/procedures');
      })
      .catch(err => {
        toast.error(err.response.data.error || 'Não foi possível cadastrar o procedimento');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    validate(procedure)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch(() => {
        // o erro já foi guardado em `validation`
      });
  }

  // nome e margem ficam na aba Geral: abre ela quando a validação reclama de um deles
  useEffect(() => {
    if (Object.keys(validation).length) setTab('main');
  }, [validation]);

  function handleChangeAddProduct(product: ProcedureProduct) {
    setProcedure(prev => {
      const existingProduct = prev.products.find(b => b.id === product.id);
      if (existingProduct) {
        return {
          ...prev,
          products: prev.products.map(b => (b.id === product.id ? { ...b, ...product } : b)),
        };
      }
      return {
        ...prev,
        products: [
          ...prev.products,
          { ...product, id: prev.products.length ? Math.max(...prev.products.map(b => b.id)) + 1 : 1 },
        ],
      };
    });
  }

  function handleDeleteProduct(product: ProcedureProduct) {
    setProcedure(prev => ({
      ...prev,
      products: prev.products.filter(b => b.id !== product.id),
    }));
  }

  function handleChangeAddDoctor(doctor: ProcedureDoctor) {
    setProcedure(prev => {
      const existingDoctor = prev.doctors.find(b => b.id === doctor.id);
      if (existingDoctor) {
        return {
          ...prev,
          doctors: prev.doctors.map(b => (b.id === doctor.id ? { ...b, ...doctor } : b)),
        };
      }
      return {
        ...prev,
        doctors: [
          ...prev.doctors,
          { ...doctor, id: prev.doctors.length ? Math.max(...prev.doctors.map(b => b.id)) + 1 : 1 },
        ],
      };
    });
  }

  function handleDeleteDoctor(doctor: ProcedureDoctor) {
    setProcedure(prev => ({
      ...prev,
      doctors: prev.doctors.filter(b => b.id !== doctor.id),
    }));
  }

  function handleChangeAddPackage(pkg: ProcedurePackage) {
    setProcedure(prev => {
      const existingPackage = prev.packages.find(b => b.id === pkg.id);
      if (existingPackage) {
        return {
          ...prev,
          packages: prev.packages.map(b => (b.id === pkg.id ? { ...b, ...pkg } : b)),
        };
      }
      return {
        ...prev,
        packages: [
          ...prev.packages,
          { ...pkg, id: prev.packages.length ? Math.max(...prev.packages.map(b => b.id)) + 1 : 1 },
        ],
      };
    });
  }

  function handleDeletePackage(pkg: ProcedurePackage) {
    setProcedure(prev => ({
      ...prev,
      packages: prev.packages.filter(b => b.id !== pkg.id),
    }));
  }

  const counts = {
    doctors: procedure.doctors.length,
    specialties: procedure.specialties.filter(item => item.active !== 0).length,
    packages: procedure.packages.length,
    products: procedure.products.length,
  };
  const isItemTab = tab === 'doctors' || tab === 'packages' || tab === 'products';

  function goBack() {
    history.push('/procedures');
  }

  return (
    <>
      <Appbar title="Procedimentos" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title="Novo procedimento" backAction={goBack}>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={`Margem ${percentFormat(procedure.profit_margin || 0)}`}
          />
        </RecordHeader>

        <WorkArea>
          <ProcedureTab onChange={newTab => setTab(newTab)} tab={tab} counts={counts} />
          <ContentPad>
            <StyledForm onSubmit={handleValidation}>
              {tab === 'packages' ? (
                <ProcedurePackages
                  procedure={procedure}
                  handleDeletePackage={handleDeletePackage}
                  handleChangeAddPackage={handleChangeAddPackage}
                />
              ) : tab === 'products' ? (
                <ProcedureProducts
                  procedure={procedure}
                  handleDeleteProduct={handleDeleteProduct}
                  handleChangeAddProduct={handleChangeAddProduct}
                />
              ) : tab === 'doctors' ? (
                <ProcedureDoctors
                  procedure={procedure}
                  handleDeleteDoctor={handleDeleteDoctor}
                  handleChangeAddDoctor={handleChangeAddDoctor}
                />
              ) : tab === 'specialties' ? (
                <ProcedureSpecialtiesForm
                  handleChange={handleChange}
                  procedure={procedure}
                  loadingSpecialties={loadingSpecialties}
                  specialties={specialties}
                />
              ) : (
                <>
                  <ProcedureForm handleChange={handleChange} procedure={procedure} validation={validation} />
                  <RecordSection title="Anestesista">
                    <ProcedureAnesthetistForm
                      anesthetist={anesthetist}
                      handleChangeAnesthetist={handleChangeAnesthetist}
                    />
                  </RecordSection>
                </>
              )}
              {isItemTab && <RecordNote>Médicos, pacotes e produtos são salvos junto com o procedimento.</RecordNote>}
              <button type="submit" style={{ display: 'none' }}></button>
            </StyledForm>
          </ContentPad>
        </WorkArea>

        <RecordActions
          saving={saving}
          saveLabel="Salvar procedimento"
          onCancel={goBack}
          onSave={() => handleValidation()}
        />
      </PageShell>
    </>
  );
};

export default NewProcedure;
